import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import Loader from '../../components/Common/Loader';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import {
    DetailCycleSuivi,
    LigneSocieteSuivi,
    ResultatRattrapage,
    useSuiviCouvertureFondamentaux,
} from '../../hooks/useFundamentalsImportLog';

// ─── Types ─────────────────────────────────────────────────────────────────

type Severity = 'CRITIQUE' | 'PROBABLE' | 'SUSPECT';
type OngletActif = 'SUIVI' | 'ANOMALIES';
type FiltreSuivi = 'TOUS' | 'RECUPERABLES' | 'INCOMPLETS' | 'INVARIANTS' | 'RETARD';

interface AnomalyDetail {
    rule: string;
    severity: Severity;
    message: string;
    valeur_suspecte: number | null;
    champ: string;
    capitalisation?: number | null;
    valeur_annee_precedente?: number | null;
}

interface AnomalyItem {
    symbole: string;
    nom_societe: string;
    exercice: number;
    period_type: string;
    closing_date: string | null;
    updated_at: string | null;
    severity: Severity;
    anomalies: AnomalyDetail[];
    nb_anomalies: number;
    revenue: number | null;
    ebitda: number | null;
    ebit: number | null;
    net_income: number | null;
    equity: number | null;
    financial_debt: number | null;
    cash: number | null;
    capitalisation: number | null;
    justification_auto?: string | null;
    auto_corrigeable?: boolean;
    action_humaine_requise?: boolean;
    diagnostic_correction?: string | null;
}

interface AnomaliesResponse {
    total: number;
    critique: number;
    probable: number;
    suspect: number;
    action_humaine_requise?: number;
    anomalies: AnomalyItem[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<Severity, { label: string; bg: string; color: string; border: string; icon: string }> = {
    CRITIQUE: { label: 'Critique', bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5', icon: '🔴' },
    PROBABLE: { label: 'Probable', bg: '#fffbeb', color: '#b45309', border: '#fcd34d', icon: '🟡' },
    SUSPECT:  { label: 'Suspect',  bg: '#eff6ff', color: '#1d4ed8', border: '#93c5fd', icon: '🔵' },
};

const STATUT_CYCLE_STYLE: Record<string, { label: string; court: string; bg: string; color: string; border: string }> = {
    COMPLET:                 { label: 'Complet (CA + RN + Equity)', court: '✓', bg: '#ecfdf5', color: '#047857', border: '#6ee7b7' },
    INCOMPLET_AVEC_RAPPORT:  { label: 'Incomplet (Rapport dispo)',  court: '◑ PDF', bg: '#fffbeb', color: '#b45309', border: '#fcd34d' },
    INCOMPLET:               { label: 'Incomplet (Sans PDF)',       court: '◑', bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
    ABSENT_AVEC_RAPPORT:     { label: 'Absent (Rapport dispo !)',   court: '❗ PDF', bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5' },
    ABSENT_SANS_PUBLICATION: { label: 'Non publié sur brvm.org',    court: '—', bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' },
    NON_PUBLIE:              { label: 'Non publié',                 court: '—', bg: '#f9fafb', color: '#9ca3af', border: '#e5e7eb' },
    PRE_INTRODUCTION:        { label: 'Pré-introduction en bourse', court: 'IPO', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
};

const RULE_LABELS: Record<string, string> = {
    R0_REVENUE_NEGATIF:            'R0 — Revenue négatif',
    R1_PLANCHER_HEURISTIQUE:       'R1 — Plancher CA',
    R1_PLANCHER_ABSOLU:            'R1 — Plancher CA',
    R2_PROFIT_SUPERIEUR_CA:        'R2 — Résultat > CA',
    R2B_PERTE_ANNUELLE_EXTREME:    'R2b — Perte annuelle extrême',
    R2B_PERTE_INTERIM_EXTREME:     'R2b — Perte intermédiaire extrême',
    R2C_RESULTAT_PART_GROUPE_INCOHERENT: 'R2c — Résultat part groupe vs publié',
    R3_EBITDA_SUPERIEUR_CA:        'R3 — EBITDA > CA',
    R3B_EBITDA_NEGATIF_EXTREME:    'R3b — EBITDA négatif extrême',
    R4_EBIT_SUPERIEUR_CA:          'R4 — EBIT > CA',
    R5_EQUITY_NEGATIF_EXTREME:     'R5 — Equity négatif extrême',
    R6_CASH_SUPERIEUR_DETTE_X10:   'R6 — Cash >> Dette',
    R7_CHAMPS_IDENTIQUES:          'R7 — Champs identiques',
    R7_REVENUE_TROP_GRAND_VS_CAPI: 'R7 — Revenue >> Capi',
    R7B_REVENUE_TROP_PETIT_VS_CAPI:'R7b — Revenue << Capi',
    R8_INVERSION_SIGNE_REVENUE:    'R8 — Inversion de signe',
    R8_VARIATION_INTERANNUELLE:    'R8 — Variation inter-années',
    R8B_VARIATION_RESULTAT_NET:    'R8b — Variation résultat net',
    R8C_VARIATION_EQUITY:          'R8c — Variation fonds propres',
    R9_ROE_EXTREME:                'R9 — ROE extrême',
    R10_PER_EXTREME:               'R10 — PER extrême',
    R11_PEG_EXTREME:               'R11 — PEG extrême',
    R12_PERIODE_CUMULEE_MAL_ETIQUETEE: 'R12 — Période cumulée mal étiquetée (Q3→9M / Q2→S1 / Q4→ANNUAL)',
    R13_CUMUL_NON_MONOTONE:        'R13 — Cumul non monotone (périmètre consolidé vs social)',
    R14_PERIODES_IDENTIQUES:       'R14 — Périodes identiques (copie comparative N-1)',
    R15_EQUITY_EGALE_RN:           'R15 — Capitaux propres = Résultat Net',
    R16_EQUITY_SAUT_ECHELLE:       'R16 — Saut d’échelle ×1000 sur Equity',
};

function fmtNumber(n: number | null | undefined): string {
    if (n == null) return '—';
    const abs = Math.abs(n);
    if (abs >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + ' Mds';
    if (abs >= 1_000_000)     return (n / 1_000_000).toFixed(2) + ' M';
    if (abs >= 1_000)         return (n / 1_000).toFixed(1) + ' K';
    return n.toLocaleString('fr-FR');
}

function fmtDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-FR');
}

// ─── Cellule de la matrice de jointure externe ──────────────────────────────

const CelluleCycle: React.FC<{ cycle: DetailCycleSuivi | undefined }> = ({ cycle }) => {
    if (!cycle) return <td style={{ padding: '6px', textAlign: 'center', color: '#9ca3af' }}>—</td>;
    const st = STATUT_CYCLE_STYLE[cycle.statut] ?? STATUT_CYCLE_STYLE.NON_PUBLIE;
    const nbDr = cycle.rapports_decouverts.length;
    const infoTooltip = [
        `${cycle.period_type} ${cycle.exercice} : ${st.label}`,
        cycle.champs_manquants.length > 0 ? `Manquant : ${cycle.champs_manquants.join(', ')}` : '',
        nbDr > 0 ? `${nbDr} rapport(s) découvert(s) (#${cycle.rapports_decouverts.map(d => d.id).join(', #')})` : '',
        cycle.dernier_journal ? `Journal #${cycle.dernier_journal.id} (${cycle.dernier_journal.source} - ${cycle.dernier_journal.statut})` : '',
    ].filter(Boolean).join(' | ');

    return (
        <td style={{ padding: '5px 4px', textAlign: 'center' }} title={infoTooltip}>
            <span style={{
                display: 'inline-block',
                minWidth: '46px',
                padding: '2px 6px',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: 600,
                backgroundColor: st.bg,
                color: st.color,
                border: `1px solid ${st.border}`,
            }}>
                {st.court}
            </span>
        </td>
    );
};

// ─── Component ──────────────────────────────────────────────────────────────

const AdminFundamentalsAnomaliesScreen: React.FC = () => {
    const navigate = useNavigate();
    const { token } = useAuth();

    // Onglet actif : Suivi (jointure externe 5 briques) ou Anomalies (R0-R11 + justifications)
    const [onglet, setOnglet] = useState<OngletActif>('SUIVI');

    // Hook de suivi de couverture & rattrapage
    const {
        suivi,
        loading: loadingSuivi,
        error: errorSuivi,
        recharger: rechargerSuivi,
        lancerRattrapage,
        rattrapageEnCours,
        corrigerCycle,
        cycleEnCorrection,
    } = useSuiviCouvertureFondamentaux();

    const [dernierBilanCycle, setDernierBilanCycle] = useState<any | null>(null);

    const [filtreSuivi, setFiltreSuivi] = useState<FiltreSuivi>('TOUS');
    const [rechercheSuivi, setRechercheSuivi] = useState('');
    const [dernierBilanRattrapage, setDernierBilanRattrapage] = useState<ResultatRattrapage | null>(null);
    const [societeDetailOuverte, setSocieteDetailOuverte] = useState<string | null>(null);

    // État Anomalies R0-R11
    const [data, setData] = useState<AnomaliesResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters Anomalies
    const [filterSeverity, setFilterSeverity] = useState<Severity | ''>('');
    const [filterRule, setFilterRule] = useState<string>('');
    const [filterExercice, setFilterExercice] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActionHumaine, setFilterActionHumaine] = useState(false);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);

    const fetchAnomalies = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/v1/admin/fundamentals/anomalies`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || `Erreur ${response.status}`);
            }
            const json: AnomaliesResponse = await response.json();
            setData(json);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Erreur inconnue');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) fetchAnomalies();
    }, [token, fetchAnomalies]);

    const executerRattrapageMaintenant = async () => {
        const res = await lancerRattrapage(0, true);
        if (res) {
            setDernierBilanRattrapage(res);
            await fetchAnomalies();
        }
    };

    const executerCorrectionCycle = async (symbole: string, exercice: number, periodType: string, discoveredReportId?: number) => {
        setDernierBilanCycle(null);
        const res = await corrigerCycle(symbole, exercice, periodType, discoveredReportId);
        if (res) {
            setDernierBilanCycle(res);
            await fetchAnomalies();
        }
    };

    // Filtrage des sociétés dans la matrice de suivi
    const societesFiltrees = useMemo(() => {
        if (!suivi) return [];
        return suivi.societes.filter((s: LigneSocieteSuivi) => {
            if (rechercheSuivi) {
                const q = rechercheSuivi.toLowerCase();
                if (!s.symbole.toLowerCase().includes(q) && !s.nom_societe.toLowerCase().includes(q)) {
                    return false;
                }
            }
            const valeursAnnuels = Object.values(s.annuels);
            const valeursPeriodiques = Object.values(s.periodiques);
            if (filtreSuivi === 'RECUPERABLES') {
                return [...valeursAnnuels, ...valeursPeriodiques].some(
                    c => c.statut === 'ABSENT_AVEC_RAPPORT' || c.statut === 'INCOMPLET_AVEC_RAPPORT'
                );
            }
            if (filtreSuivi === 'INCOMPLETS') {
                return valeursAnnuels.some(c => c.statut.startsWith('INCOMPLET'));
            }
            if (filtreSuivi === 'INVARIANTS') {
                return s.invariants.length > 0;
            }
            if (filtreSuivi === 'RETARD') {
                return valeursAnnuels.some(c => c.statut === 'ABSENT_SANS_PUBLICATION');
            }
            return true;
        });
    }, [suivi, filtreSuivi, rechercheSuivi]);

    // Compute unique exercices & rules from data
    const availableExercices = useMemo(() => {
        if (!data) return [];
        return [...new Set(data.anomalies.map(a => a.exercice))].sort((a, b) => b - a);
    }, [data]);

    const availableRules = useMemo(() => {
        if (!data) return [];
        const rules = new Set<string>();
        data.anomalies.forEach(a => a.anomalies.forEach(d => rules.add(d.rule)));
        return [...rules].sort();
    }, [data]);

    const filtered = useMemo(() => {
        if (!data) return [];
        return data.anomalies.filter(a => {
            if (filterSeverity && a.severity !== filterSeverity) return false;
            if (filterExercice && String(a.exercice) !== filterExercice) return false;
            if (filterRule && !a.anomalies.some(d => d.rule === filterRule)) return false;
            if (filterActionHumaine && !a.action_humaine_requise) return false;
            if (searchTerm) {
                const t = searchTerm.toLowerCase();
                if (!a.symbole.toLowerCase().includes(t) && !a.nom_societe.toLowerCase().includes(t)) return false;
            }
            return true;
        });
    }, [data, filterSeverity, filterExercice, filterRule, filterActionHumaine, searchTerm]);

    const rowKey = (a: AnomalyItem) => `${a.symbole}-${a.exercice}-${a.period_type}`;

    // ─── Render ─────────────────────────────────────────────────────────────

    return (
        <SiteLayout>
            <Header icon="warning" title="Contrôle & Anomalies des Fondamentaux" />

            <div style={{ padding: '20px' }}>

                {/* ── Barre d'onglets (Suivi Couverture vs Anomalies R0-R11) ── */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                        onClick={() => setOnglet('SUIVI')}
                        style={{
                            padding: '10px 18px',
                            borderRadius: '8px',
                            border: onglet === 'SUIVI' ? '2px solid #1d4ed8' : '1px solid #d1d5db',
                            background: onglet === 'SUIVI' ? '#eff6ff' : '#ffffff',
                            color: onglet === 'SUIVI' ? '#1d4ed8' : '#374151',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: 'pointer',
                        }}
                    >
                        📊 Suivi Couverture & Pipeline (2020–2026)
                        {suivi && suivi.synthese.nb_trous_recuperables > 0 && (
                            <span style={{
                                marginLeft: '8px',
                                background: '#dc2626',
                                color: '#fff',
                                padding: '2px 7px',
                                borderRadius: '10px',
                                fontSize: '11px',
                            }}>
                                {suivi.synthese.nb_trous_recuperables} à rattraper
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => setOnglet('ANOMALIES')}
                        style={{
                            padding: '10px 18px',
                            borderRadius: '8px',
                            border: onglet === 'ANOMALIES' ? '2px solid #b91c1c' : '1px solid #d1d5db',
                            background: onglet === 'ANOMALIES' ? '#fef2f2' : '#ffffff',
                            color: onglet === 'ANOMALIES' ? '#b91c1c' : '#374151',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: 'pointer',
                        }}
                    >
                        ⚠️ Anomalies & Justifications ({data ? data.total : '…'})
                    </button>

                    <div style={{ flex: 1 }} />

                    <button
                        onClick={() => navigate('/admin/journal-imports')}
                        style={{
                            background: '#ffffff',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            padding: '9px 14px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 600,
                        }}
                    >
                        📜 Journal des Imports
                    </button>

                    <button
                        onClick={executerRattrapageMaintenant}
                        disabled={rattrapageEnCours}
                        style={{
                            background: rattrapageEnCours ? '#9ca3af' : '#047857',
                            color: '#ffffff',
                            border: 'none',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            cursor: rattrapageEnCours ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: 700,
                        }}
                    >
                        {rattrapageEnCours ? '⏳ Vérification & Rattrapage en cours…' : '⚡ Vérifier & Rattraper maintenant'}
                    </button>
                </div>

                {/* ── Bannière de résultat du rattrapage ── */}
                {dernierBilanRattrapage && (
                    <div style={{
                        background: '#ecfdf5',
                        border: '1px solid #6ee7b7',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '16px',
                        color: '#065f46',
                        fontSize: '13px',
                    }}>
                        <div style={{ fontWeight: 700, marginBottom: '4px' }}>
                            ✅ {dernierBilanRattrapage.message}
                        </div>
                        {dernierBilanRattrapage.corrections_deterministes.corrections.length > 0 && (
                            <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                                {dernierBilanRattrapage.corrections_deterministes.corrections.map((c, idx) => (
                                    <li key={idx}>{c}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {/* ── Bannière de résultat de correction ciblée de cycle ── */}
                {dernierBilanCycle && (
                    <div style={{
                        background: dernierBilanCycle.corrige ? '#ecfdf5' : '#fffbeb',
                        border: `1px solid ${dernierBilanCycle.corrige ? '#6ee7b7' : '#fcd34d'}`,
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '16px',
                        color: dernierBilanCycle.corrige ? '#065f46' : '#92400e',
                        fontSize: '13px',
                    }}>
                        <div style={{ fontWeight: 700, marginBottom: '4px' }}>
                            {dernierBilanCycle.corrige ? '✅' : '⚠️'} {dernierBilanCycle.symbole} {dernierBilanCycle.period_type} {dernierBilanCycle.exercice} : {dernierBilanCycle.detail || dernierBilanCycle.message}
                        </div>
                        {dernierBilanCycle.champs_manquants_restants && dernierBilanCycle.champs_manquants_restants.length > 0 && (
                            <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                Champs encore absents : <strong>{dernierBilanCycle.champs_manquants_restants.join(', ')}</strong>
                            </div>
                        )}
                    </div>
                )}

                {/* =====================================================================
                    ONGLET 1 : SUIVI COUVERTURE & PIPELINE (JOINTURE EXTERNE 5 BRIQUES)
                   ===================================================================== */}
                {onglet === 'SUIVI' && (
                    <>
                        {loadingSuivi ? (
                            <Box><Loader message="Construction de la jointure externe (48 sociétés × 2020-2026)..." /></Box>
                        ) : errorSuivi || !suivi ? (
                            <Box>
                                <div style={{ padding: '20px', color: '#dc2626', background: '#fef2f2', borderRadius: '8px' }}>
                                    ❌ {errorSuivi || 'Données indisponibles'}
                                </div>
                            </Box>
                        ) : (
                            <>
                                {/* KPI Synthèse */}
                                <div style={{ display: 'flex', gap: '12px', marginBottom: '18px', flexWrap: 'wrap' }}>
                                    <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '14px 18px', flex: '1 1 180px' }}>
                                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#047857' }}>
                                            {suivi.synthese.taux_couverture_annuelle_pct}%
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#065f46', fontWeight: 600 }}>
                                            Couverture Annuelle ({suivi.fenetre_annuelle[0]}–{suivi.fenetre_annuelle[suivi.fenetre_annuelle.length - 1]})
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#047857', marginTop: '2px' }}>
                                            {suivi.synthese.total_annuels_complets} / {suivi.synthese.total_annuels_exigibles} exercices exigibles complets
                                        </div>
                                    </div>

                                    <div
                                        onClick={() => setFiltreSuivi(filtreSuivi === 'RECUPERABLES' ? 'TOUS' : 'RECUPERABLES')}
                                        style={{
                                            background: '#fef2f2',
                                            border: `2px solid ${filtreSuivi === 'RECUPERABLES' ? '#b91c1c' : '#fca5a5'}`,
                                            borderRadius: '10px',
                                            padding: '14px 18px',
                                            flex: '1 1 180px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#b91c1c' }}>
                                            {suivi.synthese.nb_trous_recuperables}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#991b1b', fontWeight: 600 }}>
                                            Trous / Incomplets avec PDF disponible
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '2px' }}>
                                            Dans discovered_reports (prêts à rattraper)
                                        </div>
                                    </div>

                                    <div
                                        onClick={() => setFiltreSuivi(filtreSuivi === 'INVARIANTS' ? 'TOUS' : 'INVARIANTS')}
                                        style={{
                                            background: '#fffbeb',
                                            border: `2px solid ${filtreSuivi === 'INVARIANTS' ? '#b45309' : '#fcd34d'}`,
                                            borderRadius: '10px',
                                            padding: '14px 18px',
                                            flex: '1 1 180px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#b45309' }}>
                                            {suivi.synthese.nb_invariants_bloquants}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 600 }}>
                                            Invariants B3 Bloquants
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#b45309', marginTop: '2px' }}>
                                            Sur {suivi.synthese.nb_societes_avec_invariants} société(s)
                                        </div>
                                    </div>

                                    <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '10px', padding: '14px 18px', flex: '1 1 180px' }}>
                                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#1d4ed8' }}>
                                            {suivi.synthese.nb_societes} actions
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>
                                            47 historiques + BBGC (admise 2026)
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#1d4ed8', marginTop: '2px' }}>
                                            {suivi.synthese.nb_documents_annexes_ignores} attestations CAC / AG filtrées
                                        </div>
                                    </div>
                                </div>

                                {/* Liste prioritaire des trous récupérables avec rapport disponible */}
                                {suivi.trous_recuperables.length > 0 && (
                                    <Box>
                                        <h3 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#991b1b' }}>
                                            🎯 Cycles manquants ou incomplets disposant d'un rapport financier dans discovered_reports ({suivi.trous_recuperables.length})
                                        </h3>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                                <thead>
                                                    <tr style={{ background: '#fef2f2', borderBottom: '1px solid #fca5a5' }}>
                                                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>Société</th>
                                                        <th style={{ padding: '8px 10px', textAlign: 'center' }}>Cycle attendu</th>
                                                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>État fundamentals</th>
                                                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>Rapport(s) discovered_reports</th>
                                                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>Dernière trace Journal d'Import</th>
                                                        <th style={{ padding: '8px 10px', textAlign: 'center' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {suivi.trous_recuperables.map((t, idx) => (
                                                        <tr key={`${t.symbole}-${t.exercice}-${t.period_type}-${idx}`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                            <td style={{ padding: '8px 10px', fontWeight: 700 }}>
                                                                {t.symbole}
                                                                <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 400 }}>{t.nom_societe}</div>
                                                            </td>
                                                            <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>
                                                                {t.period_type} {t.exercice}
                                                            </td>
                                                            <td style={{ padding: '8px 10px', color: '#b91c1c' }}>
                                                                {t.statut === 'ABSENT_AVEC_RAPPORT' ? 'Absent' : `Incomplet (${t.champs_manquants.join(', ')})`}
                                                            </td>
                                                            <td style={{ padding: '8px 10px' }}>
                                                                {t.rapports_decouverts.map(dr => (
                                                                    <div key={dr.id} style={{ marginBottom: '2px' }}>
                                                                        <a
                                                                            href={dr.source_url || '#'}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 600 }}
                                                                        >
                                                                            #{dr.id} ({dr.statut})
                                                                        </a>
                                                                        {' '}— {dr.title}
                                                                    </div>
                                                                ))}
                                                            </td>
                                                            <td style={{ padding: '8px 10px', color: '#4b5563' }}>
                                                                {t.dernier_journal ? (
                                                                    <span>
                                                                        <strong>#{t.dernier_journal.id}</strong> [{t.dernier_journal.source} / {t.dernier_journal.statut}]
                                                                        {t.dernier_journal.message ? ` — ${t.dernier_journal.message}` : ''}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ color: '#9ca3af' }}>Non encore simulé</span>
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '8px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                                <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                                                                    {(() => {
                                                                        const cycleKey = `${t.symbole}-${t.exercice}-${t.period_type}`;
                                                                        const isEnCorrection = cycleEnCorrection === cycleKey;
                                                                        const premierRapportId = t.rapports_decouverts[0]?.id;
                                                                        return (
                                                                            <button
                                                                                onClick={() => executerCorrectionCycle(t.symbole, t.exercice, t.period_type, premierRapportId)}
                                                                                disabled={isEnCorrection || rattrapageEnCours}
                                                                                style={{
                                                                                    background: isEnCorrection ? '#9ca3af' : '#047857',
                                                                                    color: '#fff',
                                                                                    border: 'none',
                                                                                    padding: '4px 9px',
                                                                                    borderRadius: '5px',
                                                                                    cursor: isEnCorrection ? 'not-allowed' : 'pointer',
                                                                                    fontSize: '11px',
                                                                                    fontWeight: 600,
                                                                                }}
                                                                                title="Extrait le PDF du rapport découvert via IA, écrit dans fundamentals et vérifie la complétude"
                                                                            >
                                                                                {isEnCorrection ? '⏳ Traitement…' : '⚡ Corriger'}
                                                                            </button>
                                                                        );
                                                                    })()}
                                                                    <button
                                                                        onClick={() => navigate(`/admin/fundamentals/${t.symbole}`)}
                                                                        style={{
                                                                            background: '#2563eb',
                                                                            color: '#fff',
                                                                            border: 'none',
                                                                            padding: '4px 9px',
                                                                            borderRadius: '5px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '11px',
                                                                        }}
                                                                    >
                                                                        Ouvrir fiche
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Box>
                                )}

                                {/* Matrice complète 48 sociétés × Annuels 2020-2025 + Périodiques 2025-2026 */}
                                <Box>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                                        <h2 style={{ margin: 0, fontSize: '16px' }}>
                                            📋 Matrice de Jointure Externe ({societesFiltrees.length} / {suivi.synthese.nb_societes} sociétés)
                                        </h2>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            <input
                                                type="text"
                                                placeholder="Filtrer symbole ou nom..."
                                                value={rechercheSuivi}
                                                onChange={e => setRechercheSuivi(e.target.value)}
                                                style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px' }}
                                            />
                                            <select
                                                value={filtreSuivi}
                                                onChange={e => setFiltreSuivi(e.target.value as FiltreSuivi)}
                                                style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px' }}
                                            >
                                                <option value="TOUS">Toutes les sociétés ({suivi.synthese.nb_societes})</option>
                                                <option value="RECUPERABLES">Avec rapport PDF à rattraper</option>
                                                <option value="INCOMPLETS">Exercices annuels incomplets (equity NULL)</option>
                                                <option value="INVARIANTS">Avec invariants B3 signalés</option>
                                                <option value="RETARD">Émetteurs en retard de publication</option>
                                            </select>
                                            <button
                                                onClick={rechargerSuivi}
                                                style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: '12px' }}
                                            >
                                                🔄 Actualiser
                                            </button>
                                        </div>
                                    </div>

                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                            <thead>
                                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                                    <th style={{ padding: '8px 10px', textAlign: 'left', position: 'sticky', left: 0, background: '#f8fafc' }}>Société</th>
                                                    <th style={{ padding: '8px 6px', textAlign: 'center' }}>Score</th>
                                                    {suivi.fenetre_annuelle.map(yr => (
                                                        <th key={yr} style={{ padding: '8px 6px', textAlign: 'center' }}>{yr}</th>
                                                    ))}
                                                    {suivi.periodes_intermediaires.map(p => (
                                                        <th key={p} style={{ padding: '8px 6px', textAlign: 'center', background: '#f1f5f9' }}>
                                                            {p.replace('_', ' ')}
                                                        </th>
                                                    ))}
                                                    <th style={{ padding: '8px 8px', textAlign: 'center' }}>Invariants B3</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {societesFiltrees.map(s => {
                                                    const ouverte = societeDetailOuverte === s.symbole;
                                                    return (
                                                        <React.Fragment key={s.symbole}>
                                                            <tr
                                                                onClick={() => setSocieteDetailOuverte(ouverte ? null : s.symbole)}
                                                                style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                                                            >
                                                                <td style={{ padding: '7px 10px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                    {s.symbole}
                                                                    {s.annee_introduction && (
                                                                        <span style={{ marginLeft: '6px', fontSize: '10px', background: '#dbeafe', color: '#1e40af', padding: '1px 5px', borderRadius: '4px' }}>
                                                                            IPO {s.annee_introduction}
                                                                        </span>
                                                                    )}
                                                                    <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 400 }}>{s.nom_societe}</div>
                                                                </td>
                                                                <td style={{ padding: '7px 6px', textAlign: 'center', fontWeight: 600 }}>
                                                                    {s.nb_annuels_complets}/{s.nb_annuels_exigibles}
                                                                </td>
                                                                {suivi.fenetre_annuelle.map(yr => (
                                                                    <CelluleCycle key={yr} cycle={s.annuels[String(yr)]} />
                                                                ))}
                                                                {suivi.periodes_intermediaires.map(p => (
                                                                    <CelluleCycle key={p} cycle={s.periodiques[p]} />
                                                                ))}
                                                                <td style={{ padding: '7px 8px', textAlign: 'center' }}>
                                                                    {s.invariants.length === 0 ? (
                                                                        <span style={{ color: '#10b981', fontWeight: 600 }}>OK</span>
                                                                    ) : (
                                                                        <span style={{
                                                                            background: '#fef2f2',
                                                                            color: '#b91c1c',
                                                                            border: '1px solid #fca5a5',
                                                                            padding: '2px 7px',
                                                                            borderRadius: '10px',
                                                                            fontSize: '11px',
                                                                            fontWeight: 700,
                                                                        }}>
                                                                            {s.invariants.length} alerte(s)
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            {ouverte && s.invariants.length > 0 && (
                                                                <tr style={{ background: '#fffbeb', borderBottom: '1px solid #fcd34d' }}>
                                                                    <td colSpan={3 + suivi.fenetre_annuelle.length + suivi.periodes_intermediaires.length} style={{ padding: '10px 16px' }}>
                                                                        <div style={{ fontWeight: 700, fontSize: '12px', marginBottom: '4px', color: '#92400e' }}>
                                                                            Invariants B3 détectés sur {s.symbole} :
                                                                        </div>
                                                                        {s.invariants.map((inv, idx) => (
                                                                            <div key={idx} style={{ fontSize: '12px', color: '#78350f', marginBottom: '3px' }}>
                                                                                • <strong>[{inv.severite}] {inv.code}</strong> ({inv.periode}) : {inv.message}
                                                                            </div>
                                                                        ))}
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </Box>

                                {/* Derniers Fundamentals Logs (FunctionLog) */}
                                {suivi.fundamentals_logs.length > 0 && (
                                    <Box>
                                        <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#374151' }}>
                                            🕒 Dernières exécutions de l'automate (function_logs — ImportAutomatiqueFondamentaux / Trigger 03:00 UTC)
                                        </h3>
                                        <div style={{ maxHeight: '220px', overflowY: 'auto', fontSize: '12px' }}>
                                            {suivi.fundamentals_logs.map(fl => (
                                                <div key={fl.id} style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                    <span style={{
                                                        padding: '2px 7px',
                                                        borderRadius: '6px',
                                                        fontWeight: 700,
                                                        fontSize: '10px',
                                                        background: fl.status === 'SUCCESS' ? '#ecfdf5' : fl.status === 'WARNING' ? '#fffbeb' : '#fef2f2',
                                                        color: fl.status === 'SUCCESS' ? '#047857' : fl.status === 'WARNING' ? '#b45309' : '#b91c1c',
                                                    }}>
                                                        {fl.status}
                                                    </span>
                                                    <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(fl.started_at)}</span>
                                                    <span style={{ color: '#111827' }}>{fl.message}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}

                {/* =====================================================================
                    ONGLET 2 : ANOMALIES & JUSTIFICATIONS (R0-R11)
                   ===================================================================== */}
                {onglet === 'ANOMALIES' && (
                    <>
                        {/* ── Compteurs de sévérité ── */}
                        {data && (
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                                {([
                                    { key: '', label: 'Toutes', count: data.total, bg: '#f9fafb', color: '#374151', border: '#e5e7eb' },
                                    { key: 'CRITIQUE', label: '🔴 Critique', count: data.critique, bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5' },
                                    { key: 'PROBABLE', label: '🟡 Probable', count: data.probable, bg: '#fffbeb', color: '#b45309', border: '#fcd34d' },
                                    { key: 'SUSPECT',  label: '🔵 Suspect',  count: data.suspect,  bg: '#eff6ff', color: '#1d4ed8', border: '#93c5fd' },
                                ] as Array<{ key: string; label: string; count: number; bg: string; color: string; border: string }>).map(s => (
                                    <div
                                        key={s.key}
                                        onClick={() => setFilterSeverity(s.key as Severity | '')}
                                        style={{
                                            background: s.bg,
                                            border: `2px solid ${filterSeverity === s.key ? s.color : s.border}`,
                                            borderRadius: '10px',
                                            padding: '14px 20px',
                                            cursor: 'pointer',
                                            minWidth: '120px',
                                            textAlign: 'center',
                                            transition: 'all 0.15s',
                                            transform: filterSeverity === s.key ? 'scale(1.03)' : 'scale(1)',
                                        }}
                                    >
                                        <div style={{ fontSize: '24px', fontWeight: 700, color: s.color }}>{s.count}</div>
                                        <div style={{ fontSize: '12px', color: s.color, fontWeight: 500 }}>{s.label}</div>
                                    </div>
                                ))}
                                <div style={{ flex: 1 }} />
                                <button
                                    onClick={() => setFilterActionHumaine(v => !v)}
                                    style={{
                                        background: filterActionHumaine ? '#7c2d12' : '#fff7ed',
                                        color: filterActionHumaine ? '#ffffff' : '#9a3412',
                                        border: '2px solid #ea580c',
                                        padding: '10px 16px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        alignSelf: 'center',
                                    }}
                                >
                                    🛠️ Action humaine requise ({data.action_humaine_requise ?? data.anomalies.filter(x => x.action_humaine_requise).length})
                                </button>
                                <button
                                    onClick={executerRattrapageMaintenant}
                                    disabled={rattrapageEnCours}
                                    style={{
                                        background: rattrapageEnCours ? '#9ca3af' : '#1d4ed8',
                                        color: '#ffffff',
                                        border: 'none',
                                        padding: '10px 16px',
                                        borderRadius: '8px',
                                        cursor: rattrapageEnCours ? 'not-allowed' : 'pointer',
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        alignSelf: 'center',
                                    }}
                                >
                                    {rattrapageEnCours ? '⏳ Auto-correction...' : '⚡ Lancer auto-correction'}
                                </button>
                                <button
                                    onClick={() => fetchAnomalies()}
                                    style={{
                                        background: '#f3f4f6',
                                        color: '#374151',
                                        border: 'none',
                                        padding: '10px 18px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        alignSelf: 'center',
                                    }}
                                >
                                    🔄 Rafraîchir
                                </button>
                            </div>
                        )}

                        <Box>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                                <h2 style={{ margin: 0, fontSize: '17px' }}>
                                    ⚠️ Détection d'anomalies dans les données fondamentales
                                </h2>
                                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                                    {filtered.length} entrée{filtered.length !== 1 ? 's' : ''} suspecte{filtered.length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            {/* ── Description des règles ── */}
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                marginBottom: '16px',
                                fontSize: '12px',
                                color: '#64748b',
                                lineHeight: '1.7',
                            }}>
                                <strong style={{ color: '#475569' }}>Règles appliquées (sans seuils par société) :</strong>
                                {' '}R0-R2 Cohérence Revenue/Résultat (R2c : part du groupe vs publié) &bull;
                                R3-R4 Cohérence EBITDA/EBIT &bull;
                                R5 Equity {'<'} –Revenue &bull;
                                R6 Cash {'>'} 10× dette (hors banques et dettes marginales {'<'} 2% CA) &bull;
                                R7 Champs identiques / Revenue vs capitalisation &bull;
                                R8 Variation ou inversion de signe inter-années (CA, résultat net, fonds propres) &bull;
                                <strong style={{ color: '#475569' }}> R9-R11 (sur ratios calculés) :</strong> ROE {'>'} 100% &bull;
                                PER {'>'} 100x &bull;
                                PEG (Lynch) {'>'} 10 en valeur absolue
                            </div>

                            {/* ── Filtres ── */}
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                                <input
                                    type="text"
                                    placeholder="Rechercher symbole ou société..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    style={{ padding: '9px 12px', borderRadius: '6px', border: '1px solid #d1d5db', minWidth: '220px', fontSize: '13px' }}
                                />
                                <select
                                    value={filterExercice}
                                    onChange={e => setFilterExercice(e.target.value)}
                                    style={{ padding: '9px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}
                                >
                                    <option value="">Tous les exercices</option>
                                    {availableExercices.map(y => <option key={y} value={String(y)}>{y}</option>)}
                                </select>
                                <select
                                    value={filterRule}
                                    onChange={e => setFilterRule(e.target.value)}
                                    style={{ padding: '9px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', maxWidth: '240px' }}
                                >
                                    <option value="">Toutes les règles</option>
                                    {availableRules.map(r => <option key={r} value={r}>{RULE_LABELS[r] ?? r}</option>)}
                                </select>
                                {(filterSeverity || filterExercice || filterRule || searchTerm) && (
                                    <button
                                        onClick={() => { setFilterSeverity(''); setFilterExercice(''); setFilterRule(''); setSearchTerm(''); }}
                                        style={{ padding: '9px 14px', borderRadius: '6px', border: 'none', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '13px' }}
                                    >
                                        ✕ Réinitialiser
                                    </button>
                                )}
                            </div>

                            {/* ── Contenu ── */}
                            {loading ? (
                                <Loader message="Analyse des données fondamentales en cours..." />
                            ) : error ? (
                                <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626', background: '#fef2f2', borderRadius: '8px' }}>
                                    ❌ {error}
                                </div>
                            ) : filtered.length === 0 ? (
                                <div style={{ padding: '40px', textAlign: 'center', color: '#22c55e' }}>
                                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
                                    <div style={{ fontWeight: 600, fontSize: '16px' }}>Aucune anomalie détectée</div>
                                    <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '6px' }}>
                                        Les données fondamentales semblent cohérentes avec les règles appliquées.
                                    </div>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                                <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>Société</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>Période</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>Sévérité</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>Règles</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>Justification / Contexte</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>Revenue</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>Résultat Net</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>Capitalisation</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>MAJ</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'center' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filtered.map(a => {
                                                const cfg = SEVERITY_CONFIG[a.severity];
                                                const key = rowKey(a);
                                                const isExpanded = expandedKey === key;

                                                return (
                                                    <React.Fragment key={key}>
                                                        <tr
                                                            style={{
                                                                borderBottom: isExpanded ? 'none' : '1px solid #e5e7eb',
                                                                background: isExpanded ? cfg.bg : '',
                                                                cursor: 'pointer',
                                                                transition: 'background 0.15s',
                                                            }}
                                                            onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f9fafb'; }}
                                                            onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = ''; }}
                                                            onClick={() => setExpandedKey(isExpanded ? null : key)}
                                                        >
                                                            <td style={{ padding: '11px 12px', fontWeight: 600 }}>
                                                                {a.symbole}
                                                                <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 400 }}>{a.nom_societe}</div>
                                                            </td>
                                                            <td style={{ padding: '11px 12px', textAlign: 'center' }}>
                                                                <span style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                                                                    {a.period_type} {a.exercice}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '11px 12px', textAlign: 'center' }}>
                                                                <span style={{
                                                                    background: cfg.bg,
                                                                    color: cfg.color,
                                                                    border: `1px solid ${cfg.border}`,
                                                                    padding: '3px 10px',
                                                                    borderRadius: '12px',
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                    whiteSpace: 'nowrap',
                                                                }}>
                                                                    {cfg.icon} {cfg.label}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '11px 12px', textAlign: 'center' }}>
                                                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                                                    {a.anomalies.map((d, i) => (
                                                                        <span key={i} style={{
                                                                            background: SEVERITY_CONFIG[d.severity].bg,
                                                                            color: SEVERITY_CONFIG[d.severity].color,
                                                                            border: `1px solid ${SEVERITY_CONFIG[d.severity].border}`,
                                                                            padding: '1px 6px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '10px',
                                                                            whiteSpace: 'nowrap',
                                                                        }}>
                                                                            {RULE_LABELS[d.rule]?.split('—')[0]?.trim() ?? d.rule}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td style={{ padding: '11px 12px', fontSize: '11px', color: a.justification_auto ? '#047857' : '#9ca3af', maxWidth: '260px' }}>
                                                                {a.action_humaine_requise && (
                                                                    <div style={{ marginBottom: '4px' }}>
                                                                        <span style={{
                                                                            background: '#fff7ed',
                                                                            color: '#9a3412',
                                                                            border: '1px solid #fdba74',
                                                                            padding: '1px 6px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '10px',
                                                                            fontWeight: 700,
                                                                        }}>
                                                                            🛠️ Action humaine requise
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {a.justification_auto ? `ℹ️ ${a.justification_auto}` : (a.diagnostic_correction || 'À vérifier')}
                                                            </td>
                                                            <td style={{ padding: '11px 12px', textAlign: 'right', fontFamily: 'monospace', color: a.revenue != null && a.revenue < 10_000_000 ? '#dc2626' : '#111827' }}>
                                                                {fmtNumber(a.revenue)}
                                                            </td>
                                                            <td style={{ padding: '11px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                                                                {fmtNumber(a.net_income)}
                                                            </td>
                                                            <td style={{ padding: '11px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#6b7280' }}>
                                                                {fmtNumber(a.capitalisation)}
                                                            </td>
                                                            <td style={{ padding: '11px 12px', textAlign: 'center', color: '#6b7280' }}>
                                                                {fmtDate(a.updated_at)}
                                                            </td>
                                                            <td style={{ padding: '11px 12px', textAlign: 'center' }}>
                                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                                    <button
                                                                        onClick={e => { e.stopPropagation(); navigate(`/admin/fundamentals/${a.symbole}`); }}
                                                                        style={{
                                                                            background: '#2563eb',
                                                                            color: '#fff',
                                                                            border: 'none',
                                                                            padding: '5px 10px',
                                                                            borderRadius: '5px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '11px',
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        ✏️ Corriger
                                                                    </button>
                                                                    <span style={{ color: '#9ca3af', fontSize: '16px', cursor: 'pointer' }}>
                                                                        {isExpanded ? '▲' : '▼'}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {isExpanded && (
                                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                                <td colSpan={10} style={{ padding: '0', background: cfg.bg }}>
                                                                    <div style={{ padding: '12px 20px 16px' }}>
                                                                        {a.action_humaine_requise && a.diagnostic_correction && (
                                                                            <div style={{
                                                                                background: '#fff7ed',
                                                                                border: '1px solid #fb923c',
                                                                                borderRadius: '6px',
                                                                                padding: '10px 14px',
                                                                                marginBottom: '12px',
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                alignItems: 'center',
                                                                                gap: '12px',
                                                                                flexWrap: 'wrap',
                                                                            }}>
                                                                                <div style={{ fontSize: '12px', color: '#9a3412', fontWeight: 600 }}>
                                                                                    🛠️ <strong>Escalade Action Humaine :</strong> {a.diagnostic_correction}
                                                                                </div>
                                                                                <button
                                                                                    onClick={e => { e.stopPropagation(); navigate(`/admin/fundamentals/${a.symbole}`); }}
                                                                                    style={{
                                                                                        background: '#ea580c',
                                                                                        color: '#fff',
                                                                                        border: 'none',
                                                                                        padding: '6px 12px',
                                                                                        borderRadius: '6px',
                                                                                        cursor: 'pointer',
                                                                                        fontSize: '12px',
                                                                                        fontWeight: 700,
                                                                                        whiteSpace: 'nowrap',
                                                                                    }}
                                                                                >
                                                                                    ✏️ Ouvrir et corriger {a.symbole} ({a.period_type} {a.exercice}) →
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                        <div style={{ marginBottom: '12px' }}>
                                                                            <div style={{ fontWeight: 600, fontSize: '12px', color: '#374151', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                                Anomalies détectées ({a.nb_anomalies})
                                                                            </div>
                                                                            {a.anomalies.map((d, i) => (
                                                                                <div key={i} style={{
                                                                                    display: 'flex',
                                                                                    gap: '10px',
                                                                                    alignItems: 'flex-start',
                                                                                    padding: '8px 12px',
                                                                                    background: '#fff',
                                                                                    borderRadius: '6px',
                                                                                    marginBottom: '6px',
                                                                                    border: `1px solid ${SEVERITY_CONFIG[d.severity].border}`,
                                                                                }}>
                                                                                    <span style={{
                                                                                        background: SEVERITY_CONFIG[d.severity].bg,
                                                                                        color: SEVERITY_CONFIG[d.severity].color,
                                                                                        padding: '2px 8px',
                                                                                        borderRadius: '4px',
                                                                                        fontSize: '11px',
                                                                                        fontWeight: 700,
                                                                                        whiteSpace: 'nowrap',
                                                                                        flexShrink: 0,
                                                                                    }}>
                                                                                        {RULE_LABELS[d.rule] ?? d.rule}
                                                                                    </span>
                                                                                    <span style={{ fontSize: '12px', color: '#374151', lineHeight: '1.5' }}>
                                                                                        {d.message}
                                                                                    </span>
                                                                                </div>
                                                                            ))}
                                                                        </div>

                                                                        <div style={{ fontWeight: 600, fontSize: '12px', color: '#374151', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                            Valeurs stockées
                                                                        </div>
                                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px' }}>
                                                                            {[
                                                                                { label: 'Revenue (CA)', value: a.revenue },
                                                                                { label: 'EBITDA', value: a.ebitda },
                                                                                { label: 'EBIT', value: a.ebit },
                                                                                { label: 'Résultat Net', value: a.net_income },
                                                                                { label: 'Equity', value: a.equity },
                                                                                { label: 'Dette Financière', value: a.financial_debt },
                                                                                { label: 'Trésorerie', value: a.cash },
                                                                                { label: 'Capitalisation Marché', value: a.capitalisation },
                                                                            ].map(({ label, value }) => (
                                                                                <div key={label} style={{
                                                                                    background: '#fff',
                                                                                    border: '1px solid #e5e7eb',
                                                                                    borderRadius: '6px',
                                                                                    padding: '8px 10px',
                                                                                }}>
                                                                                    <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>{label}</div>
                                                                                    <div style={{ fontSize: '13px', fontWeight: 600, fontFamily: 'monospace', color: value == null ? '#9ca3af' : '#111827' }}>
                                                                                        {fmtNumber(value)}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Box>
                    </>
                )}
            </div>
        </SiteLayout>
    );
};

export default AdminFundamentalsAnomaliesScreen;
